import { AddIcon } from '@approvalmax/ui';
import { Box, Button, Flex, Form, Spacing, Table } from '@approvalmax/ui/src/components';
import { BulkActionsPanel } from '@approvalmax/ui/src/components/BulkActionsPanel/BulkActionsPanel';
import { useSelector } from 'modules/react-redux';
import { FC, memo } from 'react';

import { LineItemsGroupEdit } from '../../../components';
import { getQBooksContext } from '../../../selectors/qbooks';
import { defaultOnChangeFieldRequiredForMatching } from '../QBooksBillForm/QBooksBillForm.helpers';
import { AccountLineItemsToolbar } from './components/AccountLineItemsToolbar/AccountLineItemsToolbar';
import {
    useAccountLineItems,
    useColumnDefinitions,
    useGroupEditItems,
    useLineItems,
    useQBooksAccountLineItemsForm,
    useTableBulkActions,
    useTableLineActions,
    useTableLinesCheckboxes,
    useTableLinesManaging,
    useTableLinesReordering,
} from './hooks';
import { qa } from './QBooksAccountLineItemsSection.constants';
import { messages } from './QBooksAccountLineItemsSection.messages';
import type { QBooksAccountLineItemsSectionProps } from './QBooksAccountLineItemsSection.types';

export const QBooksAccountLineItemsSection: FC<QBooksAccountLineItemsSectionProps> = memo((props) => {
    const { onChange = defaultOnChangeFieldRequiredForMatching, onRemoveMatching } = props;

    const context = useSelector(getQBooksContext);
    const lineActions = useTableLineActions();
    const lineItems = useLineItems();
    const accountLineItems = useAccountLineItems();

    const hasOtherLineItems = lineItems.some((li) => !li.empty);

    const { handleAddLine } = useTableLinesManaging();
    const { handleReorderLines } = useTableLinesReordering();
    const { checkedItems, onCheckedItemsChange } = useTableLinesCheckboxes();

    const { withBulkEdit, withBulkClone, withBulkRemove, handleBulkRemove, handleBulkClone, handleCancelBulkActions } =
        useTableBulkActions(accountLineItems);

    const { groupItems, isBulkEditPopupVisible, handleBulkEditPopupOpen, handleBulkEditPopupClose } =
        useGroupEditItems(accountLineItems);

    const withBulkButtons =
        checkedItems && checkedItems.length > 0 && (withBulkEdit || withBulkClone || withBulkRemove);

    const columnDefinitions = useColumnDefinitions({
        onRemoveMatching,
    });

    const { form, lines, handleFormChange } = useQBooksAccountLineItemsForm({
        accountLineItems,
        hasOtherLineItems,
        onChange,
    });

    return (
        <Box spacing='0 60' data-qa={qa()}>
            <Form form={form} onChange={handleFormChange}>
                <Flex height='60px' width='100%' alignItems='center'>
                    {withBulkButtons ? (
                        <Box width='100%'>
                            <BulkActionsPanel
                                onEdit={withBulkEdit ? handleBulkEditPopupOpen : undefined}
                                onClone={withBulkClone ? handleBulkClone : undefined}
                                onDelete={withBulkRemove ? handleBulkRemove : undefined}
                                onClose={handleCancelBulkActions}
                                selectedItemsCount={checkedItems.length}
                            />
                        </Box>
                    ) : (
                        <AccountLineItemsToolbar withTaxFeature={context.hasTaxesFeature} />
                    )}
                </Flex>

                <Table
                    bordered
                    fontSize='small'
                    data={lines}
                    columns={columnDefinitions}
                    rowActionMenuItems={lineActions}
                    checkedItems={checkedItems}
                    onCheckedItemsChange={onCheckedItemsChange}
                    onReorderRows={handleReorderLines}
                    reorderable
                    hoverable={false}
                />

                <Spacing height={8} />

                <Button size='small' color='blue80' onClick={handleAddLine} startIcon={<AddIcon />}>
                    {messages.addLine}
                </Button>

                {isBulkEditPopupVisible && <LineItemsGroupEdit onClose={handleBulkEditPopupClose} items={groupItems} />}
            </Form>
        </Box>
    );
});

QBooksAccountLineItemsSection.displayName = 'QBooksAccountLineItemsSection';
